/**
 * Lane-2 field ingest client (P1-12; server: POST /api/field/v1/events,
 * backend/src/modules/field-events).
 *
 * SOS, GPS positions and check-ins go to the field-events endpoint as events
 * with client-generated ids, so a retry of the same record is stored once:
 *   - SOS:       a UUIDv7 event_id per alarm, reused on every retry of it;
 *   - position:  event_id = the point's point_id (created when the point was
 *                recorded, persisted with it in the IndexedDB buffer);
 *   - check-in:  a UUIDv7 event_id per check-in.
 *
 * Credential: the tracker credential (POST /api/tracker/token, aud
 * position-ingest, <= 15 min) when one can be had, else the session token,
 * which the endpoint also accepts. An SOS never waits for a tracker mint: it
 * uses a cached tracker credential or goes straight out on the session token.
 * The calls go through a bare HTTP client (raw-http.ts), so an expired tracker
 * credential's 401 never signs the user out; a terminal auth code still does
 * (terminal-auth.ts).
 *
 * Legacy fallback: VITE_FIELD_INGEST_LEGACY=true (build flag) sends everything
 * to the old /personnel-tracking/* routes, and a server without the field
 * endpoint (404) gets the legacy call for that request.
 */
import { api } from './api';
import { rawPost, type RawPost, type RawResponse } from './raw-http';
import { handleTerminalAuth, onTerminalAuth, terminalAuthCode, type TerminalAuthCode } from './terminal-auth';
import { decodeJwtPayload, useAuthStore } from '@/store/auth.store';
import { useTenantStore } from '@/store/tenant.store';

export const FIELD_EVENTS_PATH = '/field/v1/events';
export const TRACKER_TOKEN_PATH = '/tracker/token';
/** Refresh a tracker credential this long before it expires. */
export const TRACKER_REFRESH_MARGIN_MS = 60_000;

// ---------------------------------------------------------------------------
// Flag
// ---------------------------------------------------------------------------

let v1Override: boolean | null = null;

/** True unless the build sets VITE_FIELD_INGEST_LEGACY=true (or a test overrides it). */
export function isFieldIngestV1Enabled(): boolean {
  if (v1Override !== null) return v1Override;
  return import.meta.env.VITE_FIELD_INGEST_LEGACY !== 'true';
}

/** For tests (null restores the build flag). */
export function setFieldIngestV1Override(value: boolean | null): void {
  v1Override = value;
}

// ---------------------------------------------------------------------------
// Ids
// ---------------------------------------------------------------------------

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_RE.test(value);
}

function randomBytes(n: number): Uint8Array {
  const bytes = new Uint8Array(n);
  globalThis.crypto.getRandomValues(bytes);
  return bytes;
}

/**
 * RFC 9562 UUID version 7: 48-bit Unix milliseconds, then random bits. The
 * server orders SOS events by it (field-events.parse.ts isUuidV7).
 */
export function uuidv7(now: number = Date.now(), random: (n: number) => Uint8Array = randomBytes): string {
  const bytes = random(16);
  let ms = Math.max(0, Math.floor(now));
  for (let i = 5; i >= 0; i--) {
    bytes[i] = ms % 256;
    ms = Math.floor(ms / 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x70; // version 7
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10xx
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

// ---------------------------------------------------------------------------
// Wire types (mirror backend field-events.types.ts)
// ---------------------------------------------------------------------------

export type FieldEventResultStatus = 'applied' | 'already_applied' | 'parent_pending' | 'rejected';

export interface FieldEventResult {
  index: number;
  event_id: string | null;
  kind: string | null;
  status: FieldEventResultStatus;
  code?: string;
  message?: string;
  retryable?: boolean;
  safety_event_id?: string;
  exercise?: boolean;
  server_event_id?: string;
  warnings?: string[];
}

export interface FieldIngestResponse {
  received_at: string;
  results: FieldEventResult[];
  summary: Record<FieldEventResultStatus, number>;
}

export interface FieldEvent {
  event_id: string;
  kind: 'sos' | 'checkin' | 'position' | 'heartbeat';
  session_id?: string;
  incident_id?: string;
  recorded_at?: string;
  /**
   * Originator stamps (migration plan §6.2): the user and tenant the event was
   * raised under. The server checks them against the credential
   * (checkOriginator), and the safety outbox and the MAUI import carry them.
   */
  user_id?: string;
  tenant_id?: string;
  payload: Record<string, unknown>;
}

/** The originator stamps for an event raised now: the signed-in user in the active tenant. */
export interface EventStamp {
  user_id?: string | null;
  tenant_id?: string | null;
}

function currentStamp(): EventStamp {
  try {
    return { user_id: useAuthStore.getState().user?.id ?? null, tenant_id: useTenantStore.getState().activeTenant?.id ?? null };
  } catch {
    return {};
  }
}

function applyStamp(event: FieldEvent, stamp: EventStamp | undefined): FieldEvent {
  if (stamp?.user_id) event.user_id = stamp.user_id;
  if (stamp?.tenant_id) event.tenant_id = stamp.tenant_id;
  return event;
}

export type IngestOutcome =
  | { kind: 'response'; status: number; body: FieldIngestResponse | null; data: unknown }
  /** The server has no field endpoint (404/405): use the legacy route. */
  | { kind: 'unavailable'; status: number }
  /** The credential is dead (signed out and wiped already). */
  | { kind: 'terminal'; code: TerminalAuthCode };

/** An error the position chunker must treat as transient (retry later with the same ids). */
export class RetryableIngestError extends Error {
  readonly retryable = true as const;
  constructor(message: string, public readonly status?: number) {
    super(message);
    this.name = 'RetryableIngestError';
  }
}

/** An error carrying the HTTP status, shaped like an axios error for classifyUploadError(). */
export class IngestHttpError extends Error {
  readonly response: { status: number; data: unknown };
  constructor(status: number, data: unknown, message?: string) {
    super(message ?? `Field ingest failed with status ${status}`);
    this.name = 'IngestHttpError';
    this.response = { status, data };
  }
}

function asIngestResponse(data: unknown): FieldIngestResponse | null {
  if (!data || typeof data !== 'object') return null;
  const results = (data as { results?: unknown }).results;
  return Array.isArray(results) ? (data as FieldIngestResponse) : null;
}

const DELIVERED: ReadonlySet<FieldEventResultStatus> = new Set(['applied', 'already_applied']);

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

export interface FieldIngestDeps {
  post: RawPost;
  now: () => number;
  sessionToken: () => string | null;
  activeTenantId: () => string | null;
}

const defaultDeps: FieldIngestDeps = {
  post: rawPost,
  now: () => Date.now(),
  sessionToken: () => useAuthStore.getState().token,
  activeTenantId: () => useTenantStore.getState().activeTenant?.id ?? null,
};

interface TrackerCredential {
  token: string;
  expiresAt: number;
  /** The session token it was minted with: a new sign-in invalidates it. */
  mintedWith: string;
  /** The tenant it was minted for (it carries that tenant): a tenant switch invalidates it. */
  tenantId: string | null;
}

/**
 * The server answered for another user or tenant than the event's stamps
 * (checkOriginator): the event was NOT stored where it belongs, so it is
 * never counted as delivered (it stays queued for the right credential).
 */
export function answeredForAnotherOriginator(result: Pick<FieldEventResult, 'warnings' | 'code'> | null | undefined): boolean {
  const flags = [...(result?.warnings ?? []), result?.code ?? ''];
  return flags.includes('TENANT_MISMATCH') || flags.includes('USER_MISMATCH');
}

export class FieldIngestClient {
  private tracker: TrackerCredential | null = null;
  private minting: Promise<TrackerCredential | null> | null = null;

  constructor(private deps: FieldIngestDeps = defaultDeps) {}

  /** Forget the tracker credential (terminal auth, sign-out, a 401 on it). */
  dropTrackerCredential(): void {
    this.tracker = null;
  }

  private cachedTracker(sessionToken: string): string | null {
    const t = this.tracker;
    if (!t || t.mintedWith !== sessionToken) return null;
    // Minted for another tenant (the user switched): its events would be journalled there.
    if (t.tenantId !== this.deps.activeTenantId()) return null;
    if (t.expiresAt - TRACKER_REFRESH_MARGIN_MS <= this.deps.now()) return null;
    return t.token;
  }

  /**
   * Mint a tracker credential with the session token. Only a device-bound
   * token (with `sid`) can: the server refuses a sign-in token. Never throws;
   * null means "use the session token".
   */
  mintTracker(sessionToken: string): Promise<TrackerCredential | null> {
    if (this.minting) return this.minting;
    const claims = decodeJwtPayload(sessionToken);
    if (!claims || typeof claims.sid !== 'string') return Promise.resolve(null);

    this.minting = (async () => {
      const headers: Record<string, string> = { Authorization: `Bearer ${sessionToken}` };
      const tenantId = this.deps.activeTenantId();
      if (tenantId) headers['X-Tenant-ID'] = tenantId;
      let res: RawResponse;
      try {
        res = await this.deps.post(TRACKER_TOKEN_PATH, {}, headers, { timeoutMs: 10_000 });
      } catch {
        return null;
      }
      const terminal = terminalAuthCode(res.status, res.data);
      if (terminal) {
        await handleTerminalAuth(terminal);
        return null;
      }
      const body = res.data as { token?: unknown; expires_at?: unknown } | null;
      if (res.status < 200 || res.status >= 300 || typeof body?.token !== 'string') return null;
      const expiresAt = typeof body.expires_at === 'string' ? Date.parse(body.expires_at) : NaN;
      if (!Number.isFinite(expiresAt)) return null;
      const credential = { token: body.token, expiresAt, mintedWith: sessionToken, tenantId };
      this.tracker = credential;
      return credential;
    })().finally(() => {
      this.minting = null;
    });
    return this.minting;
  }

  /**
   * POST a batch. `mint`: may this call wait for a tracker credential mint
   * (never for an SOS). Rejects only when no response arrived at all.
   */
  async postEvents(events: FieldEvent[], options: { mint: boolean }): Promise<IngestOutcome> {
    const sessionToken = this.deps.sessionToken();
    if (!sessionToken) throw new RetryableIngestError('Not signed in');

    let tracker = this.cachedTracker(sessionToken);
    if (!tracker && options.mint) tracker = (await this.mintTracker(sessionToken))?.token ?? null;

    const body = { batch_id: uuidv7(this.deps.now()), sent_at: new Date(this.deps.now()).toISOString(), events };
    const send = (credential: string, isTracker: boolean) => {
      const headers: Record<string, string> = { Authorization: `Bearer ${credential}` };
      // A tracker credential carries its tenant; a session token takes the active one.
      const tenantId = this.deps.activeTenantId();
      if (!isTracker && tenantId) headers['X-Tenant-ID'] = tenantId;
      return this.deps.post(FIELD_EVENTS_PATH, body, headers, { timeoutMs: 20_000 });
    };

    let res = await send(tracker ?? sessionToken, tracker !== null);
    let terminal = terminalAuthCode(res.status, res.data);
    if (!terminal && res.status === 401 && tracker !== null) {
      // The tracker credential expired or was refused: once more on the session token.
      this.dropTrackerCredential();
      res = await send(sessionToken, false);
      terminal = terminalAuthCode(res.status, res.data);
    }
    if (terminal) {
      this.dropTrackerCredential();
      await handleTerminalAuth(terminal);
      return { kind: 'terminal', code: terminal };
    }
    if (res.status === 404 || res.status === 405) return { kind: 'unavailable', status: res.status };
    return { kind: 'response', status: res.status, body: asIngestResponse(res.data), data: res.data };
  }
}

export const fieldIngestClient = new FieldIngestClient();
onTerminalAuth(() => fieldIngestClient.dropTrackerCredential());

// ---------------------------------------------------------------------------
// SOS
// ---------------------------------------------------------------------------

export interface SosInput {
  session_id?: string;
  incident_id?: string;
  lat?: number;
  lng?: number;
  message?: string;
}

export interface SosSendResult {
  acknowledged: boolean;
  via: 'field_v1' | 'legacy';
  event_id?: string;
  safety_event_id?: string;
  exercise?: boolean;
}

const SOS_RETRY_DELAYS_MS = [1_000, 3_000];

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function finiteOrUndefined(v: number | undefined): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

export function buildSosEvent(input: SosInput, eventId: string, recordedAt: string, stamp?: EventStamp): FieldEvent {
  const payload: Record<string, unknown> = { trigger: 'button' };
  const lat = finiteOrUndefined(input.lat);
  const lng = finiteOrUndefined(input.lng);
  if (lat !== undefined && lng !== undefined) {
    payload.lat = lat;
    payload.lng = lng;
  }
  if (input.message) payload.note = input.message.slice(0, 500);
  return applyStamp(
    {
      event_id: eventId,
      kind: 'sos',
      recorded_at: recordedAt,
      ...(input.session_id ? { session_id: input.session_id } : {}),
      ...(input.incident_id ? { incident_id: input.incident_id } : {}),
      payload,
    },
    stamp
  );
}

async function sendSosLegacy(input: SosInput): Promise<SosSendResult> {
  if (!input.session_id) throw new Error('An active tracking session is required to send an SOS');
  await api.post('/personnel-tracking/sos', {
    session_id: input.session_id,
    ...(finiteOrUndefined(input.lat) !== undefined && finiteOrUndefined(input.lng) !== undefined
      ? { lat: input.lat, lng: input.lng }
      : {}),
  });
  return { acknowledged: true, via: 'legacy' };
}

/**
 * Send an SOS. The event id is fixed for the life of this alarm, so every
 * retry (network loss, 5xx, 429, a non-terminal 401/403) is the same event to
 * the server, which raises it once.
 */
/**
 * Where an SOS or a check-in is kept until the server stores it
 * (offline/safety-outbox.service.ts, §6.2 step 2(a)). Optional and best
 * effort: the outbox can never delay (beyond SOS_OUTBOX_WAIT_MS) or block the send.
 */
export interface SosOutbox {
  record(event: FieldEvent, kind: 'sos' | 'checkin'): Promise<void>;
  delivered(eventId: string, result: { server_event_id?: string; safety_event_id?: string; via?: 'field_v1' | 'legacy' }): Promise<void>;
}

/** The longest the SOS path waits for the outbox write before sending anyway. */
const SOS_OUTBOX_WAIT_MS = 250;

async function bestEffort(work: () => Promise<void>): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      work().catch(() => undefined),
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, SOS_OUTBOX_WAIT_MS);
      }),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

export async function sendSos(
  input: SosInput,
  options: { client?: FieldIngestClient; retryDelaysMs?: number[]; now?: () => number; outbox?: SosOutbox; stamp?: EventStamp } = {}
): Promise<SosSendResult> {
  if (!isFieldIngestV1Enabled()) return sendSosLegacy(input);
  const client = options.client ?? fieldIngestClient;
  const now = options.now ?? Date.now;
  const delays = options.retryDelaysMs ?? SOS_RETRY_DELAYS_MS;
  const eventId = uuidv7(now());
  // Stamped with whoever raised it: the outbox, a later flush and the MAUI import all read this.
  const event = buildSosEvent(input, eventId, new Date(now()).toISOString(), options.stamp ?? currentStamp());
  // Durable copy BEFORE the first attempt (S-06): if every attempt below
  // fails, the drain re-sends this same event and hands it to the MAUI app.
  const outbox = options.outbox;
  if (outbox) await bestEffort(() => outbox.record(event, 'sos'));

  let lastError: Error = new Error('SOS not sent');
  for (let attempt = 0; attempt <= delays.length; attempt++) {
    if (attempt > 0) await sleep(delays[attempt - 1]);
    let outcome: IngestOutcome;
    try {
      outcome = await client.postEvents([event], { mint: false });
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err));
      continue;
    }
    if (outcome.kind === 'unavailable') {
      const legacy = await sendSosLegacy(input);
      // Stored by the legacy route, outside the field-events journal: recorded as such so
      // the drain never hands it off to be raised a second time.
      if (outbox) await bestEffort(() => outbox.delivered(eventId, { via: 'legacy' }));
      return legacy;
    }
    if (outcome.kind === 'terminal') {
      throw new Error('Your access was revoked. Sign in again to send an SOS.');
    }
    const result = outcome.body?.results.find((r) => r.index === 0) ?? outcome.body?.results[0];
    if (result && DELIVERED.has(result.status)) {
      // Raised, but under another user or tenant than its stamps: the outbox keeps it for the right one.
      if (outbox && !answeredForAnotherOriginator(result)) await bestEffort(() => outbox.delivered(eventId, result));
      return {
        acknowledged: true,
        via: 'field_v1',
        event_id: result.server_event_id ?? eventId,
        safety_event_id: result.safety_event_id,
        exercise: result.exercise,
      };
    }
    if (result && result.status === 'rejected' && result.retryable === false) {
      throw new Error(result.message || 'The server refused the SOS');
    }
    const status = outcome.status;
    const transient = status >= 500 || status === 401 || status === 403 || status === 408 || status === 429 || !!result;
    lastError = new IngestHttpError(status, outcome.data);
    if (!transient) throw lastError;
  }
  throw lastError;
}

// ---------------------------------------------------------------------------
// Check-in
// ---------------------------------------------------------------------------

/** Check-in types the field endpoint accepts (backend checkinPayloadSchema). */
export const FIELD_V1_CHECKIN_TYPES: ReadonlySet<string> = new Set([
  'arrival',
  'departure',
  'welfare',
  'task_start',
  'task_complete',
  'break',
]);

export interface CheckInInput {
  incident_id?: string;
  session_id?: string;
  check_in_type: string;
  lat?: number;
  lng?: number;
  message?: string;
  status?: string;
}

export function buildCheckInEvent(input: CheckInInput, eventId: string, recordedAt: string, stamp?: EventStamp): FieldEvent {
  const payload: Record<string, unknown> = { check_in_type: input.check_in_type };
  const lat = finiteOrUndefined(input.lat);
  const lng = finiteOrUndefined(input.lng);
  if (lat !== undefined && lng !== undefined) {
    payload.lat = lat;
    payload.lng = lng;
  }
  if (input.message) payload.notes = input.message.slice(0, 2000);
  return applyStamp(
    {
      event_id: eventId,
      kind: 'checkin',
      recorded_at: recordedAt,
      ...(input.session_id ? { session_id: input.session_id } : {}),
      ...(input.incident_id ? { incident_id: input.incident_id } : {}),
      payload,
    },
    stamp
  );
}

/**
 * Record a check-in. Types the field endpoint does not know (the legacy
 * 'routine', 'status_change', 'hazard', 'manual') stay on the legacy route.
 */
export async function sendCheckIn<T = unknown>(
  input: CheckInInput,
  options: { client?: FieldIngestClient; now?: () => number; outbox?: SosOutbox; stamp?: EventStamp } = {}
): Promise<T | FieldEventResult> {
  if (!isFieldIngestV1Enabled() || !FIELD_V1_CHECKIN_TYPES.has(input.check_in_type)) {
    return api.post<T>('/personnel-tracking/check-ins', input);
  }
  const client = options.client ?? fieldIngestClient;
  const now = options.now ?? Date.now;
  const event = buildCheckInEvent(input, uuidv7(now()), new Date(now()).toISOString(), options.stamp ?? currentStamp());
  // Durable copy BEFORE the first attempt (§6.2 step 2(a)): a check-in whose
  // send fails is re-sent by the drain (same event id) and handed to the MAUI app.
  const outbox = options.outbox;
  if (outbox) await bestEffort(() => outbox.record(event, 'checkin'));
  const outcome = await client.postEvents([event], { mint: true });
  if (outcome.kind === 'unavailable') {
    const legacy = await api.post<T>('/personnel-tracking/check-ins', input);
    if (outbox) await bestEffort(() => outbox.delivered(event.event_id, { via: 'legacy' }));
    return legacy;
  }
  if (outcome.kind === 'terminal') throw new Error('Your access was revoked. Sign in again.');
  const result = outcome.body?.results[0];
  if (result && DELIVERED.has(result.status)) {
    if (outbox && !answeredForAnotherOriginator(result)) await bestEffort(() => outbox.delivered(event.event_id, result));
    return result;
  }
  throw new IngestHttpError(outcome.status, outcome.data, result?.message);
}

// ---------------------------------------------------------------------------
// Positions
// ---------------------------------------------------------------------------

export interface PositionPoint {
  point_id?: string;
  lat: number;
  lng: number;
  altitude?: number;
  accuracy?: number;
  speed?: number;
  heading?: number;
  battery_percent?: number;
  recorded_at: string;
}

export interface PositionEventsUploadResult {
  received: number;
  inserted: number;
  duplicates: number;
  rejected: number;
  rejected_reasons?: Record<string, number>;
}

export function buildPositionEvent(sessionId: string, p: PositionPoint): FieldEvent {
  const payload: Record<string, unknown> = { lat: p.lat, lng: p.lng };
  for (const key of ['altitude', 'accuracy', 'speed', 'heading', 'battery_percent'] as const) {
    const v = p[key];
    if (typeof v === 'number' && Number.isFinite(v)) payload[key] = v;
  }
  return {
    event_id: p.point_id as string,
    kind: 'position',
    session_id: sessionId,
    recorded_at: p.recorded_at,
    payload,
  };
}

/** Points the field endpoint can take: every one needs a UUID point_id (its event_id). */
export function canSendAsEvents(points: PositionPoint[]): boolean {
  return points.length > 0 && points.every((p) => isUuid(p.point_id));
}

/**
 * Per-point rejections that are about the point itself (its clock reading is
 * outside the server's window, P0-15 checkRecordedAt). Re-sending the point can
 * never change the answer and the rest of the chunk was stored, so these count
 * as handled, as they did on the legacy route. Every other non-retryable
 * rejection (SESSION_NOT_OWNED, TRACKER_SCOPE_MISMATCH, INVALID_EVENT, ...) says
 * nothing was stored for that point and never will be under this credential:
 * the chunk is dead-lettered (kept on the device), never marked synced.
 */
export const POSITION_HANDLED_REJECTIONS: ReadonlySet<string> = new Set([
  'RECORDED_AT_INVALID',
  'RECORDED_AT_FUTURE',
  'RECORDED_AT_TOO_OLD',
]);

/**
 * parent_pending on a position means the server cannot see the tracking
 * session under this credential. The bridge creates sessions online before it
 * records points, so a session that stays invisible (another tenant after a
 * tenant switch, a deleted session) never resolves. Bound the re-sends: after
 * PARENT_PENDING_MAX_ATTEMPTS answers spread over at least
 * PARENT_PENDING_MIN_SPAN_MS, or once the chunk's oldest point is older than
 * PARENT_PENDING_MAX_AGE_MS, the chunk is dead-lettered (kept, not re-sent),
 * as the legacy route's 404 did.
 */
export const PARENT_PENDING_MAX_ATTEMPTS = 5;
export const PARENT_PENDING_MIN_SPAN_MS = 10 * 60_000;
export const PARENT_PENDING_MAX_AGE_MS = 6 * 60 * 60_000;

const parentPendingBySession = new Map<string, { attempts: number; firstSeenAt: number }>();

/** For tests. */
export function __resetParentPendingForTests(): void {
  parentPendingBySession.clear();
}

/** Status used for a dead-lettered chunk (classifyUploadError: permanent 4xx). */
const DEAD_LETTER_STATUS = 422;

function parentPendingVerdict(sessionId: string, points: PositionPoint[], now: number): 'retry' | 'dead_letter' {
  const entry = parentPendingBySession.get(sessionId) ?? { attempts: 0, firstSeenAt: now };
  entry.attempts += 1;
  parentPendingBySession.set(sessionId, entry);
  const oldest = points.reduce((min, p) => {
    const ms = Date.parse(p.recorded_at);
    return Number.isFinite(ms) && ms < min ? ms : min;
  }, Number.POSITIVE_INFINITY);
  const tooOld = Number.isFinite(oldest) && now - oldest >= PARENT_PENDING_MAX_AGE_MS;
  const exhausted = entry.attempts >= PARENT_PENDING_MAX_ATTEMPTS && now - entry.firstSeenAt >= PARENT_PENDING_MIN_SPAN_MS;
  return tooOld || exhausted ? 'dead_letter' : 'retry';
}

function isParentPendingAnswer(status: number, body: FieldIngestResponse | null, data: unknown): boolean {
  if (body?.results.some((r) => r.status === 'parent_pending')) return true;
  const code = (data as { error?: { code?: unknown } } | null)?.error?.code;
  return status === 409 && code === 'PARENT_PENDING';
}

/**
 * Upload one chunk (<= 500) of positions as events. Resolves when the server
 * answered for every point (stored, duplicate, or refused for its clock
 * reading). Throws a RetryableIngestError when any point must be re-sent
 * (parent pending, rate limited, partially applied, retryable rejection): the
 * chunker then keeps the whole chunk and re-sends it with the same ids, and the
 * server dedupes the ones it already stored. Throws an IngestHttpError for
 * other HTTP failures and for refusals that no re-send can fix (a session that
 * is not this user's or out of the tracker credential's scope, a session that
 * stays invisible): classifyUploadError() dead-letters those, so the points
 * stay on the device and are never marked synced.
 *
 * Resolves to null when the server has no field endpoint (404): the caller
 * uses the legacy route.
 */
export async function uploadPositionEvents(
  sessionId: string,
  points: PositionPoint[],
  options: { client?: FieldIngestClient; now?: () => number } = {}
): Promise<PositionEventsUploadResult | null> {
  const client = options.client ?? fieldIngestClient;
  const now = options.now ?? Date.now;
  const outcome = await client.postEvents(
    points.map((p) => buildPositionEvent(sessionId, p)),
    { mint: true }
  );
  if (outcome.kind === 'unavailable') return null;
  if (outcome.kind === 'terminal') throw new IngestHttpError(401, { error: { code: outcome.code, terminal: true } });

  const { status, body } = outcome;
  if (isParentPendingAnswer(status, body, outcome.data)) {
    if (parentPendingVerdict(sessionId, points, now()) === 'dead_letter') {
      parentPendingBySession.delete(sessionId);
      throw new IngestHttpError(
        DEAD_LETTER_STATUS,
        { error: { code: 'PARENT_SESSION_UNAVAILABLE', session_id: sessionId } },
        'The server cannot see this tracking session; the points are kept on the device'
      );
    }
    throw new RetryableIngestError(`Field ingest asked for a re-send (parent pending, HTTP ${status})`, status);
  }
  if (status === 409 || status === 429 || status === 503) {
    throw new RetryableIngestError(`Field ingest asked for a re-send (HTTP ${status})`, status);
  }
  if (status < 200 || status >= 300 || !body) throw new IngestHttpError(status, outcome.data);

  const out: PositionEventsUploadResult = { received: points.length, inserted: 0, duplicates: 0, rejected: 0 };
  const reasons: Record<string, number> = {};
  const refused: string[] = [];
  for (const r of body.results) {
    if (r.status === 'applied') out.inserted++;
    else if (r.status === 'already_applied') out.duplicates++;
    else if (r.retryable === true) {
      throw new RetryableIngestError(`Field ingest asked for a re-send (${r.code ?? r.status})`, status);
    } else if (r.status === 'rejected' && r.code && POSITION_HANDLED_REJECTIONS.has(r.code)) {
      out.rejected++;
      reasons[r.code] = (reasons[r.code] ?? 0) + 1;
    } else {
      refused.push(r.code ?? 'REJECTED');
    }
  }
  if (out.inserted + out.duplicates > 0) parentPendingBySession.delete(sessionId);
  if (refused.length > 0) {
    // Not stored and never will be under this credential: dead letter, never "delivered".
    throw new IngestHttpError(
      DEAD_LETTER_STATUS,
      { error: { code: refused[0], codes: Array.from(new Set(refused)) }, results: body.results },
      `Field ingest refused ${refused.length} point(s) (${Array.from(new Set(refused)).join(', ')})`
    );
  }
  if (out.rejected > 0) out.rejected_reasons = reasons;
  return out;
}
